import {usePortalTooltip} from '@github-ui/portal-tooltip/use-portal-tooltip'
import {testIdProps} from '@github-ui/test-id-props'
import {VisuallyHidden} from '@github-ui/visually-hidden'
import {Text, Token} from '@primer/react'
import {memo, type ReactNode, useCallback, useRef} from 'react'

import type {Iteration} from '../../../api/columns/contracts/iteration'
import {MemexColumnDataType, SystemColumnId} from '../../../api/columns/contracts/memex-column'
import type {PersistedOption} from '../../../api/columns/contracts/single-select'
import type {ColumnData} from '../../../api/columns/contracts/storage'
import type {Progress} from '../../../api/columns/contracts/tracks'
import type {ExtendedRepository, Label as LabelInterface, Milestone, Owner} from '../../../api/common-contracts'
import type {TrackedByItem} from '../../../api/issues-graph/contracts'
import {ItemType} from '../../../api/memex-items/item-type'
import {getInitialState} from '../../../helpers/initial-state'
import {getAllIterations, intervalDatesDescription} from '../../../helpers/iterations'
import {
  asCustomDateString,
  formatISODateString,
  isDateColumnValue,
  isNumericColumnValue,
  isSingleSelectOrIterationColumnValue,
  isTextColumnValue,
  parseTextFromHtmlStr,
} from '../../../helpers/parsing'
import {fullDisplayName} from '../../../helpers/tracked-by-formatter'
import {useBoardSidePanel} from '../../../hooks/use-side-panel'
import type {ColumnModel} from '../../../models/column-model'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {Resources} from '../../../strings'
import {SanitizedHtml} from '../../dom/sanitized-html'
import {IterationToken} from '../../fields/iteration/iteration-token'
import {LabelToken} from '../../fields/label-token'
import {LinkedPullRequestToken} from '../../fields/linked-pr-token'
import {MilestoneToken} from '../../fields/milestone-token'
import {RepositoryToken} from '../../fields/repository/repository-token'
import {ReviewersToken} from '../../fields/reviewers-token'
import {SingleSelectToken} from '../../fields/single-select/single-select-token'
import {TrackedByToken} from '../../fields/tracked-by-token'
import {TracksToken} from '../../fields/tracks/tracks-token'
import {useSearch} from '../../filter-bar/search-context'

type CardLabelProps = {
  field: ColumnModel
  columnData: ColumnData
  item: MemexItemModel
}

const useClickToFilter = (key: string, value: string | number) => {
  const {toggleFilter} = useSearch()

  return useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (event.target instanceof HTMLAnchorElement) {
        event.stopPropagation()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (!event.shiftKey && !event.metaKey) {
        // If the shift or meta key is held, we are assuming the user wants to
        // select multiple cards, in which case filtering would break
        // expectation and cause them to lose their selections.
        toggleFilter(key, value)
      }
    },
    [toggleFilter, key, value],
  )
}

const LabelledLabel = ({label, children}: {label: string | null; children: ReactNode}) => (
  <Text as="li" sx={{p: 0, m: 0, maxWidth: '100%'}}>
    {label !== null && <VisuallyHidden>{label}: </VisuallyHidden>}
    {children}
  </Text>
)

const CustomFieldLabel = ({
  content,
  fieldName,
  filterValue,
}: {
  content: string
  fieldName: string
  filterValue: string
}) => {
  const ref = useRef(null)
  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': `${fieldName}: ${parseTextFromHtmlStr(content.toString())}`,
  })

  return (
    <LabelledLabel label={fieldName}>
      <Token
        onClick={useClickToFilter(fieldName, filterValue)}
        as="button"
        ref={ref}
        text={<SanitizedHtml>{content}</SanitizedHtml>}
        {...testIdProps(`custom-label-${fieldName}`)}
        {...contentProps}
        aria-describedby={undefined}
      />
      {tooltip}
    </LabelledLabel>
  )
}

const MilestoneLabel = ({fieldName, milestone}: {fieldName: string; milestone: Milestone}) => {
  const ref = useRef<HTMLElement>(null)
  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': `${fieldName}: ${milestone.title}`,
  })

  return (
    <LabelledLabel label={fieldName}>
      <MilestoneToken
        ref={ref}
        {...contentProps}
        milestone={milestone}
        onClick={useClickToFilter(fieldName, milestone.title)}
        as="button"
      />
      {tooltip}
    </LabelledLabel>
  )
}

const IterationLabel = ({fieldName, iteration}: {fieldName: string; iteration: Iteration}) => {
  const ref = useRef<HTMLElement>(null)
  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': `${fieldName}: ${iteration.title}\n${intervalDatesDescription(iteration)}`,
  })

  return (
    <LabelledLabel label={fieldName}>
      <IterationToken
        ref={ref}
        {...contentProps}
        iteration={iteration}
        onClick={useClickToFilter(fieldName, iteration.title)}
        as="button"
      />
      {tooltip}
    </LabelledLabel>
  )
}

const TracksLabel = ({progress, item}: {progress: Progress; item: MemexItemModel}) => {
  const {openPane} = useBoardSidePanel()
  const ref = useRef<HTMLDivElement>(null)

  const handleTracksClick = useCallback(() => {
    if (!item || item.contentType !== ItemType.Issue) return
    openPane(item)
  }, [item, openPane])

  const tooltipText = Resources.progressPercentCount(progress.percent)
  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': tooltipText,
  })

  if (progress.total === 0) return null

  return (
    <LabelledLabel label="Progress">
      <TracksToken
        ref={ref}
        constantWidth
        progress={progress}
        onClick={handleTracksClick}
        {...contentProps}
        {...testIdProps('tracks-label')}
      />
      {tooltip}
    </LabelledLabel>
  )
}

const TrackedByLabel = ({
  trackedByItem,
  issueId,
  projectOwner,
}: {
  trackedByItem: TrackedByItem
  issueId: number
  projectOwner?: Owner
}) => {
  const ref = useRef(null)

  return (
    <LabelledLabel label={null}>
      <TrackedByToken
        {...testIdProps(`tracked-by-label-${trackedByItem.key.itemId}`)}
        as="button"
        ref={ref}
        onClick={useClickToFilter('tracked-by', fullDisplayName(trackedByItem))}
        trackedBy={trackedByItem}
        issueId={issueId}
        projectOwner={projectOwner}
      />
    </LabelledLabel>
  )
}

const LabelLabel = ({fieldName, label}: {fieldName: string; label: LabelInterface}) => (
  <LabelledLabel label="Label">
    <LabelToken
      label={label}
      as="button"
      onClick={useClickToFilter(fieldName, label.name)}
      {...testIdProps('issue-label')}
    />
  </LabelledLabel>
)

function SingleSelectLabel({field, option}: {field: ColumnModel; option: PersistedOption}) {
  const ref = useRef<HTMLElement>(null)
  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': `${field.name}: ${option.name}`,
  })

  return (
    <LabelledLabel label={field.name}>
      <SingleSelectToken
        option={option}
        {...contentProps}
        onClick={useClickToFilter(field.name, option.name)}
        as="button"
        ref={ref}
      />
      {tooltip}
    </LabelledLabel>
  )
}

function RepositoryLabel({fieldName, repository}: {fieldName: string; repository: ExtendedRepository}) {
  const ref = useRef<HTMLElement>(null)
  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: ref,
    'aria-label': `${fieldName}: ${repository.nameWithOwner}`,
  })

  return (
    <LabelledLabel label={fieldName}>
      <RepositoryToken
        withOwner
        repository={repository}
        {...contentProps}
        onClick={useClickToFilter(fieldName, repository.nameWithOwner)}
        as="button"
        ref={ref}
        {...testIdProps('repository-label')}
      />
      {tooltip}
    </LabelledLabel>
  )
}

const MAX_ITEM_LABEL_LENGTH = 10

export const CardLabel = memo(({field, columnData, item}: CardLabelProps) => {
  const {projectOwner} = getInitialState()

  let content: React.ReactNode = null

  switch (field.dataType) {
    case MemexColumnDataType.Labels:
      content = columnData[SystemColumnId.Labels]?.map(label => (
        <LabelLabel key={label.id} fieldName={field.name} label={label} />
      ))
      break

    case MemexColumnDataType.LinkedPullRequests:
      content = columnData[SystemColumnId.LinkedPullRequests]
        ?.slice(0, MAX_ITEM_LABEL_LENGTH)
        .map(linkedPullRequest => (
          <LabelledLabel label="Linked pull request" key={linkedPullRequest.id}>
            <LinkedPullRequestToken linkedPullRequest={linkedPullRequest} />
          </LabelledLabel>
        ))
      break

    case MemexColumnDataType.Reviewers: {
      const reviews = columnData[SystemColumnId.Reviewers]
      content = reviews && reviews.length > 0 && (
        <LabelledLabel label="Reviewers">
          <ReviewersToken reviews={reviews} sx={{bg: 'canvas.default'}} />
        </LabelledLabel>
      )
      break
    }

    case MemexColumnDataType.Milestone: {
      const milestone = columnData[SystemColumnId.Milestone]
      content = milestone && <MilestoneLabel milestone={milestone} fieldName={field.name} />
      break
    }

    case MemexColumnDataType.Iteration: {
      const iterationValue = columnData[field.id]

      const iteration =
        iterationValue && isSingleSelectOrIterationColumnValue(iterationValue)
          ? getAllIterations(field).find(o => o.id === iterationValue.id)
          : undefined

      content = iteration && <IterationLabel iteration={iteration} fieldName={field.name} />
      break
    }

    case MemexColumnDataType.Tracks: {
      const progress = columnData[SystemColumnId.Tracks]
      content = progress && <TracksLabel progress={progress} item={item} />
      break
    }

    case MemexColumnDataType.TrackedBy:
      content = columnData[SystemColumnId.TrackedBy]
        ?.slice(0, MAX_ITEM_LABEL_LENGTH)
        .map(trackedByItem => (
          <TrackedByLabel
            key={trackedByItem.key.itemId}
            issueId={item.itemId()}
            trackedByItem={trackedByItem}
            projectOwner={projectOwner}
          />
        ))
      break

    case MemexColumnDataType.SingleSelect: {
      const optionValue = columnData[field.id]

      const option =
        optionValue && isSingleSelectOrIterationColumnValue(optionValue)
          ? field.settings.options?.find(o => o.id === optionValue.id)
          : undefined

      content = option && <SingleSelectLabel option={option} field={field} />
      break
    }

    case MemexColumnDataType.Date: {
      const dateValue = columnData[field.id]

      if (dateValue && isDateColumnValue(dateValue)) {
        const dateStr = asCustomDateString(dateValue)
        const filterValue = formatISODateString(new Date(dateValue.value))

        content = dateStr && <CustomFieldLabel content={dateStr} fieldName={field.name} filterValue={filterValue} />
      }
      break
    }

    case MemexColumnDataType.Number: {
      const numberValue = columnData[field.id]

      content = numberValue && isNumericColumnValue(numberValue) && (
        <CustomFieldLabel
          content={numberValue.value.toString()}
          fieldName={field.name}
          filterValue={numberValue.value.toString()}
        />
      )
      break
    }

    case MemexColumnDataType.Text: {
      const textValue = columnData[field.id]

      content = textValue && isTextColumnValue(textValue) && (
        <CustomFieldLabel content={textValue.raw} fieldName={field.name} filterValue={textValue.raw} />
      )
      break
    }

    case MemexColumnDataType.Repository: {
      const repository = columnData[SystemColumnId.Repository]

      content = repository && <RepositoryLabel fieldName={field.name} repository={repository} />
      break
    }

    case MemexColumnDataType.Assignees:
    case MemexColumnDataType.Title:
      break

    default:
      // Ensure that we account for every possible field type as new ones are added
      ensureUnreachable(field)
  }

  return <>{content}</>
})
CardLabel.displayName = 'CardLabel'

function ensureUnreachable(_: never) {
  /*noop*/
}

try{ LabelledLabel.displayName ||= 'LabelledLabel' } catch {}
try{ CustomFieldLabel.displayName ||= 'CustomFieldLabel' } catch {}
try{ MilestoneLabel.displayName ||= 'MilestoneLabel' } catch {}
try{ IterationLabel.displayName ||= 'IterationLabel' } catch {}
try{ TracksLabel.displayName ||= 'TracksLabel' } catch {}
try{ TrackedByLabel.displayName ||= 'TrackedByLabel' } catch {}
try{ LabelLabel.displayName ||= 'LabelLabel' } catch {}
try{ SingleSelectLabel.displayName ||= 'SingleSelectLabel' } catch {}
try{ RepositoryLabel.displayName ||= 'RepositoryLabel' } catch {}